/**
 * @fileoverview Composed Section - Component Library
 *
 * Purpose: Display all composed UI components (accordion, dialogs, menus, etc.)
 * Used by: Component library main page
 */

import { ComponentShowcase } from "./-ui.component-showcase";
import { ComponentHeader } from "./-ui.component-header";
import { VariantGrid, VariantItem } from "./-ui.variant-grid";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { cn } from "@/utils/misc";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/ui/composed/sections/accordion";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/ui/composed/sections/collapsible";
import {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
} from "@/ui/composed/menus/command";
import { Calendar } from "@/ui/composed/data-display/calendar";
import type { DateRange } from "react-day-picker";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/ui/composed/data-display/carousel";
import { Card, CardContent } from "@/ui/primitives/card";
import { Label } from "@/ui/primitives/label";
import { Textarea } from "@/ui/primitives/textarea";
import { Badge } from "@/ui/primitives/badge";
import { Checkbox } from "@/ui/primitives/checkbox";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/ui/composed/sections/resizable";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/ui/composed/sections/tabs";
import { useToast } from "@/ui/hooks/use-toast";
import { toast as sonnerToast } from "sonner";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/ui/composed/overlays/sheet";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/ui/composed/overlays/drawer";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/ui/composed/overlays/hover-card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/ui/composed/data-display/chart";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/ui/composed/overlays/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from "@/ui/composed/menus/dropdown-menu";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSeparator,
  ContextMenuCheckboxItem,
} from "@/ui/composed/menus/context-menu";
import {
  Settings,
  User,
  LogOut,
  HelpCircle,
  Info,
  ChevronsUpDown,
  Calculator,
  Smile,
  Calendar as CalendarIcon,
  Check,
  CalendarDays,
  ArrowUpDown,
} from "lucide-react";
import { useState, useEffect } from "react";

// Helper component for dropdown menu with checkboxes
function DropdownMenuCheckboxExample() {
  const [showStatusBar, setShowStatusBar] = useState(true);
  const [showActivityBar, setShowActivityBar] = useState(false);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">View Options</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>Appearance</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={showStatusBar}
          onCheckedChange={setShowStatusBar}
        >
          Status Bar
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={showActivityBar}
          onCheckedChange={setShowActivityBar}
        >
          Activity Bar
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Command Dialog Demo with keyboard shortcut
function CommandDialogDemo() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "j" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={() => setOpen(true)}>
          Open Command Palette
        </Button>
        <p className="text-sm text-muted-foreground">
          or press{" "}
          <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
            <span className="text-xs">⌘</span>J
          </kbd>
        </p>
      </div>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Type a command or search..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Suggestions">
            <CommandItem onSelect={() => setOpen(false)}>
              <CalendarIcon className="mr-2 h-4 w-4" />
              <span>Calendar</span>
            </CommandItem>
            <CommandItem onSelect={() => setOpen(false)}>
              <Smile className="mr-2 h-4 w-4" />
              <span>Search Emoji</span>
            </CommandItem>
            <CommandItem onSelect={() => setOpen(false)}>
              <Calculator className="mr-2 h-4 w-4" />
              <span>Calculator</span>
            </CommandItem>
          </CommandGroup>
          <CommandGroup heading="Settings">
            <CommandItem onSelect={() => setOpen(false)}>
              <User className="mr-2 h-4 w-4" />
              <span>Profile</span>
              <CommandShortcut>⌘P</CommandShortcut>
            </CommandItem>
            <CommandItem onSelect={() => setOpen(false)}>
              <Settings className="mr-2 h-4 w-4" />
              <span>Settings</span>
              <CommandShortcut>⌘S</CommandShortcut>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </div>
  );
}

// Calendar demos
function CalendarSingleDemo() {
  const [date, setDate] = useState<Date | undefined>(new Date());
  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <Calendar
          mode="single"
          selected={date}
          onSelect={setDate}
          className="rounded-md border"
        />
      </div>
      {date && (
        <p className="text-center text-sm text-muted-foreground">
          Selected: {date.toLocaleDateString()}
        </p>
      )}
    </div>
  );
}

function CalendarRangeDemo() {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();

  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <Calendar
          mode="range"
          selected={dateRange}
          onSelect={setDateRange}
          className="rounded-md border"
          numberOfMonths={2}
        />
      </div>
      {dateRange?.from && (
        <p className="text-center text-sm text-muted-foreground">
          {dateRange.to
            ? `${dateRange.from.toLocaleDateString()} - ${dateRange.to.toLocaleDateString()}`
            : dateRange.from.toLocaleDateString()}
        </p>
      )}
    </div>
  );
}

function CalendarMultipleDemo() {
  const [dates, setDates] = useState<Date[] | undefined>([]);

  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <Calendar
          mode="multiple"
          selected={dates}
          onSelect={setDates}
          className="rounded-md border"
        />
      </div>
      {dates && dates.length > 0 && (
        <p className="text-center text-sm text-muted-foreground">
          Selected {dates.length} date{dates.length > 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
}

// Combobox Demo
function ComboboxDemo() {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");

  const frameworks = [
    { value: "next.js", label: "Next.js" },
    { value: "sveltekit", label: "SvelteKit" },
    { value: "nuxt.js", label: "Nuxt.js" },
    { value: "remix", label: "Remix" },
    { value: "astro", label: "Astro" },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {value
            ? frameworks.find((framework) => framework.value === value)?.label
            : "Select framework..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput placeholder="Search framework..." />
          <CommandList>
            <CommandEmpty>No framework found.</CommandEmpty>
            <CommandGroup>
              {frameworks.map((framework) => (
                <CommandItem
                  key={framework.value}
                  value={framework.value}
                  onSelect={(currentValue) => {
                    setValue(currentValue === value ? "" : currentValue);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === framework.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {framework.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Chart data and configs
const chartData = [
  { month: "Jan", desktop: 186, mobile: 80 },
  { month: "Feb", desktop: 305, mobile: 200 },
  { month: "Mar", desktop: 237, mobile: 120 },
  { month: "Apr", desktop: 73, mobile: 190 },
  { month: "May", desktop: 209, mobile: 130 },
  { month: "Jun", desktop: 214, mobile: 140 },
];

const chartConfig = {
  desktop: {
    label: "Desktop",
    color: "hsl(var(--primary))",
  },
  mobile: {
    label: "Mobile",
    color: "hsl(var(--muted))",
  },
} satisfies ChartConfig;

function BarChartDemo() {
  return (
    <ChartContainer config={chartConfig} className="min-h-[200px] w-full">
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" />
        <YAxis />
        <ChartTooltip content={<ChartTooltipContent />} />
        <Bar dataKey="desktop" fill="var(--color-desktop)" />
        <Bar dataKey="mobile" fill="var(--color-mobile)" />
      </BarChart>
    </ChartContainer>
  );
}

function LineChartDemo() {
  return (
    <ChartContainer config={chartConfig} className="min-h-[200px] w-full">
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" />
        <YAxis />
        <ChartTooltip content={<ChartTooltipContent />} />
        <Line
          type="monotone"
          dataKey="desktop"
          stroke="var(--color-desktop)"
          strokeWidth={2}
        />
        <Line
          type="monotone"
          dataKey="mobile"
          stroke="var(--color-mobile)"
          strokeWidth={2}
        />
      </LineChart>
    </ChartContainer>
  );
}

function AreaChartDemo() {
  return (
    <ChartContainer config={chartConfig} className="min-h-[200px] w-full">
      <AreaChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" />
        <YAxis />
        <ChartTooltip content={<ChartTooltipContent />} />
        <Area
          type="monotone"
          dataKey="desktop"
          fill="var(--color-desktop)"
          stroke="var(--color-desktop)"
          fillOpacity={0.2}
        />
        <Area
          type="monotone"
          dataKey="mobile"
          fill="var(--color-mobile)"
          stroke="var(--color-mobile)"
          fillOpacity={0.2}
        />
      </AreaChart>
    </ChartContainer>
  );
}

// Toast Demo
function ToastDemo() {
  const { toast } = useToast();

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        variant="outline"
        onClick={() => {
          toast({
            title: "Scheduled: Catch up",
            description: "Friday, February 10, 2023 at 5:57 PM",
          });
        }}
      >
        Show Toast
      </Button>
      <Button
        variant="outline"
        onClick={() => {
          toast({
            title: "Success!",
            description: "Your changes have been saved.",
          });
        }}
      >
        Success Toast
      </Button>
      <Button
        variant="outline"
        onClick={() => {
          toast({
            variant: "destructive",
            title: "Uh oh! Something went wrong.",
            description: "There was a problem with your request.",
          });
        }}
      >
        Destructive Toast
      </Button>
    </div>
  );
}

// Date Picker Demo
function DatePickerDemo() {
  const [date, setDate] = useState<Date>();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-muted-foreground",
          )}
        >
          <CalendarDays className="mr-2 h-4 w-4" />
          {date ? date.toLocaleDateString() : "Pick a date"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={setDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

// Data Table Demo (simplified version)
function DataTableDemo() {
  const [sorting, setSorting] = useState<{
    column: string;
    direction: "asc" | "desc";
  } | null>(null);

  const data = [
    {
      id: 1,
      name: "John Doe",
      email: "john@example.com",
      status: "active",
      amount: 250,
    },
    {
      id: 2,
      name: "Jane Smith",
      email: "jane@example.com",
      status: "pending",
      amount: 150,
    },
    {
      id: 3,
      name: "Bob Johnson",
      email: "bob@example.com",
      status: "inactive",
      amount: 350,
    },
    {
      id: 4,
      name: "Alice Williams",
      email: "alice@example.com",
      status: "active",
      amount: 420,
    },
    {
      id: 5,
      name: "Charlie Brown",
      email: "charlie@example.com",
      status: "active",
      amount: 180,
    },
  ];

  const [filterText, setFilterText] = useState("");
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  const filteredData = data.filter(
    (row) =>
      row.name.toLowerCase().includes(filterText.toLowerCase()) ||
      row.email.toLowerCase().includes(filterText.toLowerCase()),
  );

  const sortedData = sorting
    ? [...filteredData].sort((a, b) => {
        const aVal = a[sorting.column as keyof typeof a];
        const bVal = b[sorting.column as keyof typeof b];
        if (sorting.direction === "asc") {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      })
    : filteredData;

  const toggleSort = (column: string) => {
    setSorting((current) => {
      if (current?.column === column) {
        return current.direction === "asc"
          ? { column, direction: "desc" }
          : null;
      }
      return { column, direction: "asc" };
    });
  };

  const toggleRow = (id: number) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleAll = () => {
    if (selectedRows.size === sortedData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(sortedData.map((r) => r.id)));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Input
          placeholder="Filter by name or email..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="max-w-sm"
        />
        <p className="ml-auto text-sm text-muted-foreground">
          {selectedRows.size} of {sortedData.length} row(s) selected
        </p>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={
                    selectedRows.size === sortedData.length &&
                    sortedData.length > 0
                  }
                  onCheckedChange={toggleAll}
                />
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  className="-ml-3 h-8"
                  onClick={() => toggleSort("name")}
                >
                  Name
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            ) : (
              sortedData.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={selectedRows.has(row.id) ? "selected" : undefined}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedRows.has(row.id)}
                      onCheckedChange={() => toggleRow(row.id)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        row.status === "active"
                          ? "default"
                          : row.status === "pending"
                            ? "secondary"
                            : "destructive"
                      }
                    >
                      {row.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    ${row.amount.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function ComposedSection() {
  return (
    <>
      {/* Accordion */}
      <div id="accordion" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Accordion"
          description="Collapsible content sections with smooth animations."
          filePath="src/ui/composed/sections/accordion.tsx"
        />

        <ComponentShowcase
          title="Single Open"
          description="Only one item can be open at a time"
        >
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger>Is it accessible?</AccordionTrigger>
              <AccordionContent>
                Yes. It adheres to the WAI-ARIA design pattern.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>Is it styled?</AccordionTrigger>
              <AccordionContent>
                Yes. It comes with default styles that matches the other
                components&apos; aesthetic.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger>Is it animated?</AccordionTrigger>
              <AccordionContent>
                Yes. It&apos;s animated by default, but you can disable it if
                you prefer.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </ComponentShowcase>

        <ComponentShowcase
          title="Multiple Open"
          description="Multiple items can be open simultaneously"
        >
          <Accordion type="multiple" className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger>Can I open multiple items?</AccordionTrigger>
              <AccordionContent>
                Yes! When using type=&quot;multiple&quot;, you can have several
                sections open at once.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>How do I close an item?</AccordionTrigger>
              <AccordionContent>
                Simply click on the trigger again to collapse the content.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger>
                Is keyboard navigation supported?
              </AccordionTrigger>
              <AccordionContent>
                Yes. Use arrow keys to navigate between items and Enter/Space to
                toggle.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </ComponentShowcase>
      </div>

      {/* Dialogs */}
      <div id="dialogs" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Dialogs"
          description="Modal dialogs with backdrop, focus trap, and accessibility features."
          filePath="src/ui/composed/overlays/dialog.tsx"
        />

        <ComponentShowcase
          title="Dialog Examples"
          description="Modal dialogs for confirmations, forms, and important information"
        >
          <VariantGrid columns={3}>
            <VariantItem label="Simple Dialog">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline">Open Dialog</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Dialog Title</DialogTitle>
                    <DialogDescription>
                      This is a simple dialog with a title and description.
                    </DialogDescription>
                  </DialogHeader>
                  <p className="text-sm text-muted-foreground">
                    Dialog content goes here.
                  </p>
                </DialogContent>
              </Dialog>
            </VariantItem>

            <VariantItem label="With Footer">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline">With Actions</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Confirm Action</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to proceed?
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline">Cancel</Button>
                    <Button>Confirm</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </VariantItem>

            <VariantItem label="Destructive">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="destructive">Delete Item</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Are you absolutely sure?</DialogTitle>
                    <DialogDescription>
                      This action cannot be undone. This will permanently delete
                      the item.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline">Cancel</Button>
                    <Button variant="destructive">Delete</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>
      </div>

      {/* Alert Dialogs */}
      <div id="alert-dialogs" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Alert Dialogs"
          description="Confirmation dialogs for critical actions that require user attention."
          filePath="src/ui/composed/overlays/alert-dialog.tsx"
        />

        <ComponentShowcase
          title="Alert Dialog Examples"
          description="Modal dialogs for confirmations and critical decisions"
        >
          <VariantGrid columns={2}>
            <VariantItem label="Delete Confirmation">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">Delete Account</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Are you absolutely sure?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete
                      your account and remove your data from our servers.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction>Delete Account</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </VariantItem>

            <VariantItem label="Confirmation Dialog">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button>Proceed</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Continue with this action?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This will make changes to your account settings.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction>Continue</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>
      </div>

      {/* Popovers */}
      <div id="popovers" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Popovers"
          description="Floating content containers for additional information and actions."
          filePath="src/ui/composed/overlays/popover.tsx"
        />

        <ComponentShowcase
          title="Popover Examples"
          description="Click-triggered floating content"
        >
          <VariantGrid columns={3}>
            <VariantItem label="Simple Popover">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline">Open Popover</Button>
                </PopoverTrigger>
                <PopoverContent>
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Popover Title</h4>
                    <p className="text-sm text-muted-foreground">
                      This is a simple popover with content.
                    </p>
                  </div>
                </PopoverContent>
              </Popover>
            </VariantItem>

            <VariantItem label="With Form">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline">Settings</Button>
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium">Dimensions</h4>
                    <div className="space-y-2">
                      <Input placeholder="Width" />
                      <Input placeholder="Height" />
                    </div>
                    <Button size="sm" className="w-full">
                      Apply
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </VariantItem>

            <VariantItem label="Info Popover">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Info className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <p className="text-sm text-muted-foreground">
                    Additional information appears here when you click the info
                    icon.
                  </p>
                </PopoverContent>
              </Popover>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>
      </div>

      {/* Tooltips */}
      <div id="tooltips" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Tooltips"
          description="Hover tooltips for providing contextual help and information."
          filePath="src/ui/composed/overlays/tooltip.tsx"
        />

        <ComponentShowcase
          title="Tooltip Examples"
          description="Hover to see tooltip (wrap in TooltipProvider)"
        >
          <TooltipProvider>
            <VariantGrid columns={3}>
              <VariantItem label="Simple Tooltip">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline">Hover me</Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This is a tooltip</p>
                  </TooltipContent>
                </Tooltip>
              </VariantItem>

              <VariantItem label="Icon with Tooltip">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Click for help</p>
                  </TooltipContent>
                </Tooltip>
              </VariantItem>

              <VariantItem label="Rich Content">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline">Rich Tooltip</Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="space-y-1">
                      <p className="font-medium">Keyboard Shortcut</p>
                      <p className="text-xs">⌘K to open command palette</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </VariantItem>
            </VariantGrid>
          </TooltipProvider>
        </ComponentShowcase>
      </div>

      {/* Dropdown Menus */}
      <div id="dropdown-menus" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Dropdown Menus"
          description="Click-triggered dropdown menus with keyboard navigation."
          filePath="src/ui/composed/menus/dropdown-menu.tsx"
        />

        <ComponentShowcase
          title="Dropdown Menu Examples"
          description="Various dropdown menu configurations"
        >
          <VariantGrid columns={3}>
            <VariantItem label="Simple Menu">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">Open Menu</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem>Profile</DropdownMenuItem>
                  <DropdownMenuItem>Settings</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </VariantItem>

            <VariantItem label="With Icons">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <User className="mr-2 h-4 w-4" />
                    Account
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </VariantItem>

            <VariantItem label="With Checkboxes">
              <DropdownMenuCheckboxExample />
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>
      </div>

      {/* Collapsible */}
      <div id="collapsible" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Collapsible"
          description="Expandable/collapsible content container with trigger."
          filePath="src/ui/composed/sections/collapsible.tsx"
        />

        <ComponentShowcase
          title="Collapsible Examples"
          description="Click buttons to expand/collapse content"
        >
          <div className="max-w-md space-y-4">
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  Can I use this in my project?
                  <ChevronsUpDown className="h-4 w-4" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 rounded-md border p-4">
                <p className="text-sm text-muted-foreground">
                  Yes! This component is built with Radix UI and styled with
                  Tailwind CSS. You can copy and paste it into your project.
                </p>
              </CollapsibleContent>
            </Collapsible>

            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  Is it accessible?
                  <ChevronsUpDown className="h-4 w-4" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 rounded-md border p-4">
                <p className="text-sm text-muted-foreground">
                  Yes. It adheres to the WAI-ARIA design pattern and includes
                  proper keyboard navigation support.
                </p>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </ComponentShowcase>
      </div>

      {/* Command */}
      <div id="command" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Command"
          description="Searchable command palette for quick actions and navigation."
          filePath="src/ui/composed/menus/command.tsx"
        />

        <ComponentShowcase
          title="Command Dialog (⌘J)"
          description="Press ⌘J (or Ctrl+J) to open the command palette"
        >
          <CommandDialogDemo />
        </ComponentShowcase>

        <ComponentShowcase
          title="Inline Command"
          description="Command palette embedded in page (try typing to search)"
        >
          <div className="max-w-md">
            <Command className="rounded-lg border shadow-md">
              <CommandInput placeholder="Type a command or search..." />
              <CommandList>
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandGroup heading="Suggestions">
                  <CommandItem>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    <span>Calendar</span>
                  </CommandItem>
                  <CommandItem>
                    <Smile className="mr-2 h-4 w-4" />
                    <span>Search Emoji</span>
                  </CommandItem>
                  <CommandItem>
                    <Calculator className="mr-2 h-4 w-4" />
                    <span>Calculator</span>
                  </CommandItem>
                </CommandGroup>
                <CommandGroup heading="Settings">
                  <CommandItem>
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                    <CommandShortcut>⌘P</CommandShortcut>
                  </CommandItem>
                  <CommandItem>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                    <CommandShortcut>⌘S</CommandShortcut>
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        </ComponentShowcase>
      </div>

      {/* Date Picker */}
      <div id="date-picker" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Date Picker"
          description="Date picker combining Calendar and Popover components."
          filePath="src/ui/composed/data-display/calendar.tsx + overlays/popover.tsx (pattern)"
        />

        <ComponentShowcase
          title="Date Picker Example"
          description="Click to open calendar in a popover"
        >
          <div className="max-w-md">
            <DatePickerDemo />
          </div>
        </ComponentShowcase>
      </div>

      {/* Calendar */}
      <div id="calendar" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Calendar"
          description="Interactive date picker calendar component with multiple modes."
          filePath="src/ui/composed/data-display/calendar.tsx"
        />

        <ComponentShowcase
          title="Single Date Selection"
          description="Select a single date (click to select/deselect)"
        >
          <CalendarSingleDemo />
        </ComponentShowcase>

        <ComponentShowcase
          title="Date Range Selection"
          description="Select a date range (click start date, then end date)"
        >
          <CalendarRangeDemo />
        </ComponentShowcase>

        <ComponentShowcase
          title="Multiple Date Selection"
          description="Select multiple dates (click to toggle dates)"
        >
          <CalendarMultipleDemo />
        </ComponentShowcase>

        <ComponentShowcase
          title="With Dropdown (Month/Year)"
          description="Calendar with month and year dropdown selectors"
        >
          <div className="flex justify-center">
            <Calendar
              mode="single"
              className="rounded-md border"
              captionLayout="dropdown"
            />
          </div>
        </ComponentShowcase>
      </div>

      {/* Data Table */}
      <div id="data-table" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Data Table"
          description="Advanced data table with sorting, filtering, and row selection."
          filePath="src/ui/composed/data-display/table.tsx (pattern)"
        />

        <ComponentShowcase
          title="Data Table Example"
          description="Click column headers to sort, use filter to search, check rows to select"
        >
          <DataTableDemo />
        </ComponentShowcase>
      </div>

      {/* Combobox */}
      <div id="combobox" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Combobox"
          description="Searchable select dropdown combining Popover and Command."
          filePath="src/ui/composed/combobox.tsx (pattern)"
        />

        <ComponentShowcase
          title="Combobox Example"
          description="Autocomplete select with search (click to open, type to filter)"
        >
          <div className="max-w-md">
            <ComboboxDemo />
          </div>
        </ComponentShowcase>
      </div>

      {/* Chart */}
      <div id="chart" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Chart"
          description="Data visualization charts powered by Recharts."
          filePath="src/ui/composed/data-display/chart.tsx"
        />

        <ComponentShowcase
          title="Bar Chart"
          description="Simple bar chart showing monthly data"
        >
          <BarChartDemo />
        </ComponentShowcase>

        <ComponentShowcase
          title="Line Chart"
          description="Line chart with multiple data series"
        >
          <LineChartDemo />
        </ComponentShowcase>

        <ComponentShowcase
          title="Area Chart"
          description="Area chart with gradient fill"
        >
          <AreaChartDemo />
        </ComponentShowcase>
      </div>

      {/* Carousel */}
      <div id="carousel" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Carousel"
          description="Sliding carousel for images, cards, or content."
          filePath="src/ui/composed/data-display/carousel.tsx"
        />

        <ComponentShowcase
          title="Carousel Examples"
          description="Swipeable carousel with previous/next controls"
        >
          <div className="mx-auto max-w-xs">
            <Carousel>
              <CarouselContent>
                {[1, 2, 3, 4, 5].map((num) => (
                  <CarouselItem key={num}>
                    <Card>
                      <CardContent className="flex aspect-square items-center justify-center p-6">
                        <span className="text-4xl font-semibold">{num}</span>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </div>
        </ComponentShowcase>
      </div>

      {/* Drawer */}
      <div id="drawer" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Drawer"
          description="Bottom-sliding drawer for mobile-friendly modals and actions."
          filePath="src/ui/composed/overlays/drawer.tsx"
        />

        <ComponentShowcase
          title="Drawer Examples"
          description="Click to open drawer (swipe down to close)"
        >
          <VariantGrid columns={2}>
            <VariantItem label="Simple Drawer">
              <Drawer>
                <DrawerTrigger asChild>
                  <Button variant="outline">Open Drawer</Button>
                </DrawerTrigger>
                <DrawerContent>
                  <DrawerHeader>
                    <DrawerTitle>Drawer Title</DrawerTitle>
                    <DrawerDescription>
                      This is a drawer component
                    </DrawerDescription>
                  </DrawerHeader>
                  <div className="p-4">
                    <p className="text-sm text-muted-foreground">
                      Drawer content goes here. You can swipe down or click
                      outside to close.
                    </p>
                  </div>
                </DrawerContent>
              </Drawer>
            </VariantItem>

            <VariantItem label="With Footer">
              <Drawer>
                <DrawerTrigger asChild>
                  <Button variant="outline">Edit Profile</Button>
                </DrawerTrigger>
                <DrawerContent>
                  <DrawerHeader>
                    <DrawerTitle>Edit profile</DrawerTitle>
                    <DrawerDescription>
                      Make changes to your profile
                    </DrawerDescription>
                  </DrawerHeader>
                  <div className="space-y-4 p-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input id="name" placeholder="Your name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bio">Bio</Label>
                      <Textarea id="bio" placeholder="Tell us about yourself" />
                    </div>
                  </div>
                  <DrawerFooter>
                    <Button>Save changes</Button>
                    <DrawerClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DrawerClose>
                  </DrawerFooter>
                </DrawerContent>
              </Drawer>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>
      </div>

      {/* Hover Card */}
      <div id="hover-card" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Hover Card"
          description="Rich content card that appears on hover."
          filePath="src/ui/composed/overlays/hover-card.tsx"
        />

        <ComponentShowcase
          title="Hover Card Examples"
          description="Hover over the elements to see cards"
        >
          <div className="flex gap-4">
            <HoverCard>
              <HoverCardTrigger asChild>
                <Button variant="link">@username</Button>
              </HoverCardTrigger>
              <HoverCardContent className="w-80">
                <div className="flex justify-between space-x-4">
                  <div className="space-y-1">
                    <h4 className="text-sm font-semibold">@username</h4>
                    <p className="text-sm text-muted-foreground">
                      Software engineer and open source contributor.
                    </p>
                    <div className="flex items-center pt-2">
                      <span className="text-xs text-muted-foreground">
                        Joined December 2021
                      </span>
                    </div>
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>

            <HoverCard>
              <HoverCardTrigger asChild>
                <Button variant="link">Hover for info</Button>
              </HoverCardTrigger>
              <HoverCardContent>
                <div className="space-y-2">
                  <p className="text-sm">
                    This is a hover card with additional context and
                    information.
                  </p>
                </div>
              </HoverCardContent>
            </HoverCard>
          </div>
        </ComponentShowcase>
      </div>

      {/* Data Table */}
      <div id="data-table" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Data Table"
          description="Advanced data table with sorting, filtering, and pagination (TanStack Table)."
          filePath="src/ui/composed/table.tsx (requires @tanstack/react-table)"
        />

        <ComponentShowcase
          title="Data Table Example"
          description="Interactive table with sorting (click headers), filtering, and row selection"
        >
          <DataTableDemo />
        </ComponentShowcase>
      </div>

      {/* Table */}
      <div id="table" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Table"
          description="Data table component for displaying structured data."
          filePath="src/ui/composed/data-display/table.tsx"
        />

        <ComponentShowcase
          title="Table Example"
          description="Basic data table with hover states"
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">John Doe</TableCell>
                <TableCell>john@example.com</TableCell>
                <TableCell>
                  <Badge>Active</Badge>
                </TableCell>
                <TableCell className="text-right">$250.00</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Jane Smith</TableCell>
                <TableCell>jane@example.com</TableCell>
                <TableCell>
                  <Badge variant="secondary">Pending</Badge>
                </TableCell>
                <TableCell className="text-right">$150.00</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Bob Johnson</TableCell>
                <TableCell>bob@example.com</TableCell>
                <TableCell>
                  <Badge variant="destructive">Inactive</Badge>
                </TableCell>
                <TableCell className="text-right">$350.00</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </ComponentShowcase>
      </div>

      {/* Resizable */}
      <div id="resizable" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Resizable"
          description="Resizable split panel layouts."
          filePath="src/ui/composed/sections/resizable.tsx"
        />

        <ComponentShowcase
          title="Resizable Examples"
          description="Drag the handles to resize panels"
        >
          <div className="space-y-6">
            <div>
              <p className="mb-3 text-sm text-muted-foreground">
                Horizontal Split
              </p>
              <ResizablePanelGroup
                direction="horizontal"
                className="min-h-[200px] rounded-lg border"
              >
                <ResizablePanel defaultSize={50}>
                  <div className="flex h-full items-center justify-center p-6">
                    <span className="font-semibold">Left Panel</span>
                  </div>
                </ResizablePanel>
                <ResizableHandle />
                <ResizablePanel defaultSize={50}>
                  <div className="flex h-full items-center justify-center p-6">
                    <span className="font-semibold">Right Panel</span>
                  </div>
                </ResizablePanel>
              </ResizablePanelGroup>
            </div>

            <div>
              <p className="mb-3 text-sm text-muted-foreground">
                Vertical Split
              </p>
              <ResizablePanelGroup
                direction="vertical"
                className="min-h-[200px] rounded-lg border"
              >
                <ResizablePanel defaultSize={50}>
                  <div className="flex h-full items-center justify-center p-6">
                    <span className="font-semibold">Top Panel</span>
                  </div>
                </ResizablePanel>
                <ResizableHandle />
                <ResizablePanel defaultSize={50}>
                  <div className="flex h-full items-center justify-center p-6">
                    <span className="font-semibold">Bottom Panel</span>
                  </div>
                </ResizablePanel>
              </ResizablePanelGroup>
            </div>
          </div>
        </ComponentShowcase>
      </div>

      {/* Sheet */}
      <div id="sheet" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Sheet"
          description="Side panel that slides in from screen edges."
          filePath="src/ui/composed/overlays/sheet.tsx"
        />

        <ComponentShowcase
          title="Sheet Examples"
          description="Click to open sheets from different sides"
        >
          <VariantGrid columns={4}>
            <VariantItem label="From Right (Default)">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline">Open</Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Edit profile</SheetTitle>
                    <SheetDescription>
                      Make changes to your profile here.
                    </SheetDescription>
                  </SheetHeader>
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="sheet-name">Name</Label>
                      <Input id="sheet-name" placeholder="Your name" />
                    </div>
                  </div>
                  <SheetFooter>
                    <SheetClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </SheetClose>
                    <Button>Save changes</Button>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            </VariantItem>

            <VariantItem label="From Left">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline">Open</Button>
                </SheetTrigger>
                <SheetContent side="left">
                  <SheetHeader>
                    <SheetTitle>Navigation</SheetTitle>
                    <SheetDescription>Mobile navigation menu</SheetDescription>
                  </SheetHeader>
                </SheetContent>
              </Sheet>
            </VariantItem>

            <VariantItem label="From Top">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline">Open</Button>
                </SheetTrigger>
                <SheetContent side="top">
                  <SheetHeader>
                    <SheetTitle>Notifications</SheetTitle>
                  </SheetHeader>
                </SheetContent>
              </Sheet>
            </VariantItem>

            <VariantItem label="From Bottom">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline">Open</Button>
                </SheetTrigger>
                <SheetContent side="bottom">
                  <SheetHeader>
                    <SheetTitle>Actions</SheetTitle>
                  </SheetHeader>
                </SheetContent>
              </Sheet>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>
      </div>

      {/* Tabs */}
      <div id="tabs" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Tabs"
          description="Organize content into switchable tabbed panels."
          filePath="src/ui/composed/sections/tabs.tsx"
        />

        <ComponentShowcase
          title="Tabs Example"
          description="Click tabs to switch between content panels"
        >
          <Tabs defaultValue="account" className="w-full max-w-md">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="account">Account</TabsTrigger>
              <TabsTrigger value="password">Password</TabsTrigger>
            </TabsList>
            <TabsContent value="account" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name-tab">Name</Label>
                <Input id="name-tab" placeholder="Your name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="username-tab">Username</Label>
                <Input id="username-tab" placeholder="@username" />
              </div>
              <Button>Save changes</Button>
            </TabsContent>
            <TabsContent value="password" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-tab">Current password</Label>
                <Input id="current-tab" type="password" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-tab">New password</Label>
                <Input id="new-tab" type="password" />
              </div>
              <Button>Update password</Button>
            </TabsContent>
          </Tabs>
        </ComponentShowcase>
      </div>

      {/* Toast */}
      <div id="toast" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Toast"
          description="Toast notifications using Radix UI (use Sonner for simpler API)."
          filePath="src/ui/composed/notifications/toast.tsx"
        />

        <ComponentShowcase
          title="Toast Example"
          description="Click to trigger toast notifications"
        >
          <ToastDemo />
        </ComponentShowcase>
      </div>

      {/* Sonner */}
      <div id="sonner" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Sonner"
          description="Beautiful toast notifications with simple API (recommended)."
          filePath="src/ui/composed/notifications/sonner.tsx"
        />

        <ComponentShowcase
          title="Sonner Examples"
          description="Click buttons to trigger different toast types"
        >
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => sonnerToast("Event has been created")}
            >
              Default Toast
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                sonnerToast("Event has been created", {
                  description: "Monday, January 3rd at 6:00pm",
                })
              }
            >
              With Description
            </Button>
            <Button
              variant="outline"
              onClick={() => sonnerToast.success("Changes saved successfully")}
            >
              Success
            </Button>
            <Button
              variant="outline"
              onClick={() => sonnerToast.error("Something went wrong")}
            >
              Error
            </Button>
            <Button
              variant="outline"
              onClick={() => sonnerToast.info("New updates available")}
            >
              Info
            </Button>
            <Button
              variant="outline"
              onClick={() => sonnerToast.warning("Disk space running low")}
            >
              Warning
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                sonnerToast("Event created", {
                  action: {
                    label: "Undo",
                    onClick: () => {},
                  },
                })
              }
            >
              With Action
            </Button>
          </div>
        </ComponentShowcase>
      </div>

      {/* Context Menus */}
      <div id="context-menus" className="scroll-mt-24 space-y-8">
        <ComponentHeader
          title="Context Menus"
          description="Right-click context menus for contextual actions."
          filePath="src/ui/composed/menus/context-menu.tsx"
        />

        <ComponentShowcase
          title="Context Menu Examples"
          description="Right-click the boxes to see context menus"
        >
          <VariantGrid columns={2}>
            <VariantItem label="Simple Context Menu">
              <ContextMenu>
                <ContextMenuTrigger asChild>
                  <div className="flex h-32 w-full items-center justify-center rounded-md border border-dashed text-sm">
                    Right click me
                  </div>
                </ContextMenuTrigger>
                <ContextMenuContent>
                  <ContextMenuItem>Copy</ContextMenuItem>
                  <ContextMenuItem>Cut</ContextMenuItem>
                  <ContextMenuItem>Paste</ContextMenuItem>
                  <ContextMenuSeparator />
                  <ContextMenuItem>Delete</ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            </VariantItem>

            <VariantItem label="With Checkboxes">
              <ContextMenu>
                <ContextMenuTrigger asChild>
                  <div className="flex h-32 w-full items-center justify-center rounded-md border border-dashed text-sm">
                    Right click for options
                  </div>
                </ContextMenuTrigger>
                <ContextMenuContent>
                  <ContextMenuCheckboxItem checked>
                    Show Grid
                  </ContextMenuCheckboxItem>
                  <ContextMenuCheckboxItem>Show Rulers</ContextMenuCheckboxItem>
                  <ContextMenuSeparator />
                  <ContextMenuItem>Reset View</ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            </VariantItem>
          </VariantGrid>
        </ComponentShowcase>
      </div>
    </>
  );
}
